import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchYachtById, clearCurrentYacht } from "../store/slices/yachtsSlice";
import {
  submitInquiry,
  resetInquiryState,
} from "../store/slices/inquiriesSlice";
import { Row } from "../components/ui/Glass";
import { Container } from "../components/ui/Section";
import {
  StaggerReveal,
  MotionItem,
  fadeUp,
  fadeUpSmall,
  fadeRight,
  scaleIn,
} from "../components/ui/Motion";
import { YachtDetailSkeleton } from "../components/ui/Skeleton";
import Lightbox from "../components/ui/Lightbox";
import { AnimatedNumber, AnimatedSpec } from "../components/ui/AnimatedNumber";
import { mediaUrl, thumbVariant } from "../utils/media";
import { formatCabins } from "../utils/format";
import {
  NotFound,
  BackLink,
  PageWrapper,
  GallerySection,
  GalleryGrid,
  GallerySlot,
  ContentSection,
  ContentGrid,
  Breadcrumbs,
  YachtBrand,
  YachtTitle,
  Tagline,
  PriceTag,
  Description,
  SpecsTitle,
  SpecsGrid,
  SpecItem,
  EngineList,
  ExternalLinks,
  GallerySubsection,
  GalleryHeading,
  ThumbGrid,
  ThumbLink,
  DeckPlanGrid,
  DeckPlanCard,
  Divider,
  Sidebar,
  InquiryCard,
  InquiryTitle,
  InquirySubtitle,
  FormField,
  Label,
  Input,
  Textarea,
  SubmitBtn,
} from "./styles/YachtDetailPage.styles";
import { ReactComponent as NotFoundIcon } from "../assets/icons/sad-smiley.svg";

function buildPhotoList(yacht) {
  const exterior = (yacht.galleryExterior || []).map((img) => ({
    src: mediaUrl(img.large),
    thumb: mediaUrl(img.thumb || thumbVariant(img.large)),
    category: "exterior",
  }));
  const interior = (yacht.galleryInterior || []).map((img) => ({
    src: mediaUrl(img.large),
    thumb: mediaUrl(img.thumb || thumbVariant(img.large)),
    category: "interior",
  }));
  const plans = (yacht.deckPlans || []).map((plan) => ({
    src: mediaUrl(plan.large),
    thumb: mediaUrl(plan.thumb || thumbVariant(plan.large)),
    category: "plan",
    caption: plan.name,
  }));
  const extras = [];
  if (yacht.heroImage)
    extras.push({ src: mediaUrl(yacht.heroImage), category: "hero" });
  if (yacht.lifestyleImage)
    extras.push({
      src: mediaUrl(yacht.lifestyleImage),
      category: "lifestyle",
    });

  // Hero triplet preference: exterior[0], exterior[1], interior[0].
  // Fall back to remaining exterior/interior items if any slot is empty.
  const triplet = [];
  const used = new Set();
  const pick = (item) => {
    if (item && !used.has(item)) {
      used.add(item);
      triplet.push(item);
    }
  };
  pick(exterior[0]);
  pick(exterior[1]);
  pick(interior[0]);
  // Top-up if some slot is missing
  for (const it of [...exterior, ...interior, ...extras, ...plans]) {
    if (triplet.length >= 3) break;
    pick(it);
  }

  // Final order: triplet first, then everything else (deduped)
  const rest = [...exterior, ...interior, ...extras, ...plans].filter(
    (it) => !used.has(it),
  );
  const list = [...triplet, ...rest];

  list.forEach((p, idx) => {
    p.idx = idx;
  });
  return list;
}

function YachtDetailPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { current, currentLoading, items, error } = useSelector(
    (state) => state.yachts,
  );
  const fromList = items.find((y) => String(y.id) === String(id));
  const yacht = current || fromList || null;
  const {
    submitting,
    success: inquirySuccess,
    error: inquiryError,
  } = useSelector((state) => state.inquiries);
  const prevSuccess = useRef(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    dispatch(fetchYachtById(id));
    return () => {
      dispatch(clearCurrentYacht());
      dispatch(resetInquiryState());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (inquirySuccess && !prevSuccess.current) {
      toast.success("Inquiry sent! We'll get back to you shortly.");
    }
    prevSuccess.current = inquirySuccess;
  }, [inquirySuccess]);

  useEffect(() => {
    if (inquiryError) {
      toast.error("Failed to send inquiry. Please try again.");
    }
  }, [inquiryError]);

  const photoList = useMemo(
    () => (yacht ? buildPhotoList(yacht) : []),
    [yacht],
  );

  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);

  // Show the skeleton both while the fetch is in flight and on the very
  // first render (before the effect dispatches it). "Not found" is reserved
  // for an actually failed/empty fetch — otherwise direct links flash the
  // error screen while the yacht is still loading.
  if (!yacht && (currentLoading || !error)) {
    return (
      <PageWrapper>
        <GallerySection>
          <Container>
            <YachtDetailSkeleton />
          </Container>
        </GallerySection>
      </PageWrapper>
    );
  }

  if (!yacht) {
    return (
      <PageWrapper>
        <GallerySection>
          <Container>
            <NotFound>
              <NotFoundIcon />
              <h2>Yacht Not Found</h2>
              <p>
                The yacht you're looking for doesn't exist or has been removed.
              </p>
              <BackLink to="/yachts">Browse All Yachts</BackLink>
            </NotFound>
          </Container>
        </GallerySection>
      </PageWrapper>
    );
  }

  const heroSlots = photoList.slice(0, 3);
  const heroSlotIds = new Set(heroSlots.map((p) => p.idx));
  const exteriorList = photoList.filter(
    (p) => p.category === "exterior" && !heroSlotIds.has(p.idx),
  );
  const interiorList = photoList.filter(
    (p) => p.category === "interior" && !heroSlotIds.has(p.idx),
  );
  const deckPlanList = photoList.filter(
    (p) => p.category === "plan" && !heroSlotIds.has(p.idx),
  );

  const specs = [
    { label: "Type", value: yacht.subtype || yacht.type, animate: true },
    { label: "Year", value: yacht.year, animate: true },
    {
      label: "Length",
      value: yacht.length ? `${yacht.length} m` : null,
      animate: true,
    },
    {
      label: "Beam",
      value: yacht.beam ? `${yacht.beam} m` : null,
      animate: true,
    },
    {
      label: "Draft",
      value: yacht.draft ? `${yacht.draft} m` : null,
      animate: true,
    },
    { label: "Guests", value: yacht.guests, animate: true },
    {
      label: "Cabins",
      value: formatCabins(yacht.cabins) ? yacht.cabins : null,
      animate: true,
    },
    { label: "Crew", value: yacht.crew, animate: true },
    {
      label: "Max Speed",
      value: yacht.maxSpeedRange || yacht.maxSpeed,
      animate: true,
    },
    { label: "Cruising Speed", value: yacht.cruisingSpeed, animate: true },
    { label: "Fuel Capacity", value: yacht.fuelCapacity, animate: true },
    { label: "Water Capacity", value: yacht.waterCapacity, animate: true },
    { label: "Black Water Tank", value: yacht.blackWaterTank, animate: true },
    { label: "Displacement", value: yacht.displacement, animate: true },
  ].filter((s) => s.value != null && s.value !== "");

  const engineOptions = Array.isArray(yacht.engineOptions)
    ? yacht.engineOptions
    : [];

  const externalLinks = [
    yacht.brochureUrl && { label: "Brochure", href: yacht.brochureUrl },
    yacht.techSpecUrl && { label: "Tech Spec", href: yacht.techSpecUrl },
    yacht.officialPage && { label: "Official Page", href: yacht.officialPage },
  ].filter(Boolean);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(
      submitInquiry({ ...formData, yachtId: yacht.id, source: "yacht-detail" }),
    );
  };

  const renderSlot = (photo) => {
    if (!photo) return <GallerySlot>Photo coming soon</GallerySlot>;
    return (
      <GallerySlot
        onClick={() => openLightbox(photo.idx)}
        style={{ cursor: "zoom-in" }}
      >
        <img src={photo.src} alt={yacht.name} />
      </GallerySlot>
    );
  };

  return (
    <PageWrapper>
      <GallerySection>
        <Container>
          <StaggerReveal>
            <MotionItem $variants={scaleIn}>
              <GalleryGrid>
                {renderSlot(heroSlots[0])}
                {renderSlot(heroSlots[1])}
                {renderSlot(heroSlots[2])}
              </GalleryGrid>
            </MotionItem>
          </StaggerReveal>
        </Container>
      </GallerySection>

      <ContentSection>
        <Container>
          <ContentGrid>
            <StaggerReveal slow>
              <MotionItem $variants={fadeUpSmall}>
                <Breadcrumbs>
                  <Link to="/">Home</Link> / <Link to="/yachts">Yachts</Link> /{" "}
                  {yacht.name}
                </Breadcrumbs>
              </MotionItem>

              <MotionItem $variants={fadeUp}>
                <YachtBrand>{yacht.brand?.name}</YachtBrand>
                <Row>
                  <YachtTitle>{yacht.name}</YachtTitle>
                </Row>
                {yacht.tagline && <Tagline>{yacht.tagline}</Tagline>}
                <PriceTag>
                  {yacht.price == null ? (
                    "Contact for price"
                  ) : (
                    <>
                      €<AnimatedNumber value={yacht.price} useCommas />
                    </>
                  )}
                </PriceTag>
              </MotionItem>

              <MotionItem $variants={fadeUpSmall}>
                {yacht.description && (
                  <Description>{yacht.description}</Description>
                )}
              </MotionItem>

              {externalLinks.length > 0 && (
                <MotionItem $variants={fadeUpSmall}>
                  <ExternalLinks>
                    {externalLinks.map((l) => (
                      <a
                        key={l.label}
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {l.label} →
                      </a>
                    ))}
                  </ExternalLinks>
                </MotionItem>
              )}

              <MotionItem $variants={fadeUp}>
                <Divider />
                <SpecsTitle>Technical Specifications</SpecsTitle>
                <SpecsGrid>
                  {specs.map(({ label, value, animate }) => (
                    <SpecItem key={label}>
                      <span className="spec-label">{label}</span>
                      <p>{animate ? <AnimatedSpec value={value} /> : value}</p>
                    </SpecItem>
                  ))}
                </SpecsGrid>
              </MotionItem>

              {engineOptions.length > 0 && (
                <MotionItem $variants={fadeUp}>
                  <Divider />
                  <SpecsTitle>
                    {engineOptions.length > 1 ? "Engine Options" : "Engine"}
                  </SpecsTitle>
                  <EngineList>
                    {engineOptions.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </EngineList>
                </MotionItem>
              )}

              {exteriorList.length > 0 && (
                <MotionItem $variants={fadeUp}>
                  <GallerySubsection>
                    <GalleryHeading>Exterior</GalleryHeading>
                    <ThumbGrid>
                      {exteriorList.map((photo) => (
                        <ThumbLink
                          key={photo.idx}
                          as="div"
                          role="button"
                          onClick={() => openLightbox(photo.idx)}
                          style={{ cursor: "zoom-in" }}
                        >
                          <img
                            src={photo.thumb || photo.src}
                            alt={`${yacht.name} exterior`}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              if (e.currentTarget.src !== photo.src) {
                                e.currentTarget.src = photo.src;
                              }
                            }}
                          />
                        </ThumbLink>
                      ))}
                    </ThumbGrid>
                  </GallerySubsection>
                </MotionItem>
              )}

              {interiorList.length > 0 && (
                <MotionItem $variants={fadeUp}>
                  <GallerySubsection>
                    <GalleryHeading>Interior</GalleryHeading>
                    <ThumbGrid>
                      {interiorList.map((photo) => (
                        <ThumbLink
                          key={photo.idx}
                          as="div"
                          role="button"
                          onClick={() => openLightbox(photo.idx)}
                          style={{ cursor: "zoom-in" }}
                        >
                          <img
                            src={photo.thumb || photo.src}
                            alt={`${yacht.name} interior`}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              if (e.currentTarget.src !== photo.src) {
                                e.currentTarget.src = photo.src;
                              }
                            }}
                          />
                        </ThumbLink>
                      ))}
                    </ThumbGrid>
                  </GallerySubsection>
                </MotionItem>
              )}

              {deckPlanList.length > 0 && (
                <MotionItem $variants={fadeUp}>
                  <GallerySubsection>
                    <GalleryHeading>Deck Plans</GalleryHeading>
                    <DeckPlanGrid>
                      {deckPlanList.map((photo) => (
                        <DeckPlanCard
                          key={photo.idx}
                          as="div"
                          role="button"
                          onClick={() => openLightbox(photo.idx)}
                          style={{ cursor: "zoom-in" }}
                        >
                          <div className="label">{photo.caption}</div>
                          <div className="frame">
                            <img
                              src={photo.thumb || photo.src}
                              alt={photo.caption}
                              loading="lazy"
                              decoding="async"
                              onError={(e) => {
                                if (e.currentTarget.src !== photo.src) {
                                  e.currentTarget.src = photo.src;
                                }
                              }}
                            />
                          </div>
                        </DeckPlanCard>
                      ))}
                    </DeckPlanGrid>
                  </GallerySubsection>
                </MotionItem>
              )}
            </StaggerReveal>

            <Sidebar slow>
              <MotionItem $variants={fadeRight}>
                <InquiryCard>
                  {inquirySuccess ? (
                    <div style={{ textAlign: "center", padding: "2rem 0" }}>
                      <InquiryTitle>Thank you!</InquiryTitle>
                      <InquirySubtitle>
                        Your inquiry has been sent. We'll get back to you
                        shortly.
                      </InquirySubtitle>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <InquiryTitle>Interested?</InquiryTitle>
                      <InquirySubtitle>
                        Send us an inquiry about the {yacht.name}
                      </InquirySubtitle>

                      <FormField>
                        <Label>Full Name</Label>
                        <Input
                          type="text"
                          name="fullName"
                          placeholder="John Doe"
                          required
                          value={formData.fullName}
                          onChange={handleChange}
                        />
                      </FormField>
                      <FormField>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          name="email"
                          placeholder="john@example.com"
                          required
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </FormField>
                      <FormField>
                        <Label>Phone</Label>
                        <Input
                          type="tel"
                          name="phone"
                          placeholder="+381 ..."
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </FormField>
                      <FormField>
                        <Label>Message</Label>
                        <Textarea
                          name="message"
                          placeholder="I'd like to know more about this yacht..."
                          value={formData.message}
                          onChange={handleChange}
                        />
                      </FormField>

                      <SubmitBtn type="submit" disabled={submitting}>
                        {submitting ? "Sending..." : "Send Inquiry"}
                      </SubmitBtn>
                    </form>
                  )}
                </InquiryCard>
              </MotionItem>
            </Sidebar>
          </ContentGrid>
        </Container>
      </ContentSection>

      <Lightbox
        images={photoList}
        activeIdx={lightboxIdx}
        setActiveIdx={setLightboxIdx}
        onClose={closeLightbox}
      />
    </PageWrapper>
  );
}

export default YachtDetailPage;
